/**
 * Merchant name normalisation. `merchantKeyOf` turns what a receipt or a bank
 * row calls the seller into a stable key for merchant memory and duplicate
 * detection: case, punctuation, company-form suffixes (AB/HB/KB …), card
 * processor prefixes ("K*", "PAYPAL *") and trailing store numbers are
 * removed. Pure, no database access.
 */

/** Company-form and legal suffixes that carry no identity. */
const SUFFIX_TOKENS: ReadonlySet<string> = new Set([
  'ab',
  'hb',
  'kb',
  'ek',
  'för',
  'publ',
  'aktiebolag',
  'handelsbolag',
  'kommanditbolag',
  'ltd',
  'limited',
  'inc',
  'llc',
  'plc',
  'gmbh',
  'oy',
  'oyj',
  'as',
  'asa',
  'aps',
  'bv',
  'sa',
  'srl',
  'sarl',
  'co',
  'corp',
]);

/** Card processor / payment platform prefixes seen on bank statements. */
const PREFIX_PATTERN = /^(?:k\*|kortköp|kortkop|card purchase|paypal|klarna|izettle|zettle|sumup|sq|stripe|shopify|google|apple\.com\/bill)\s*[*_\-:]?\s*/i;

/** "nr 123", "#123", "butik 45", "store 12" – store identifiers. */
const STORE_NUMBER_PATTERN = /\b(?:nr|no|butik|store|shop|filial|kassa|term)\.?\s*\d+\b/g;

/** Domains: "www.spotify.com", "spotify.se" → "spotify". */
const DOMAIN_PATTERN = /\b(?:www\.)?([a-z0-9-]+)\.(?:se|com|net|org|io|nu|dk|no|fi|eu|co\.uk)\b/gi;

/**
 * Lower-case, punctuation-free, suffix-free key. Keeps Swedish letters
 * (å ä ö) and internal digits ("7 eleven", "coop x:tra" → "coop x tra") but
 * drops trailing numeric tokens ("pressbyrån 1234" → "pressbyrån").
 */
export function merchantKeyOf(name: string): string {
  let s = name.normalize('NFKC').trim().toLowerCase();
  s = s.replace(PREFIX_PATTERN, '');
  s = s.replace(DOMAIN_PATTERN, '$1');
  s = s.replace(STORE_NUMBER_PATTERN, ' ');
  s = s.replace(/[^\p{L}\p{N}]+/gu, ' ');
  let tokens = s.split(' ').filter(Boolean);
  // Strip company-form suffixes anywhere after the first token.
  tokens = tokens.filter((t, i) => i === 0 || !SUFFIX_TOKENS.has(t));
  // Drop trailing pure-number tokens (store numbers) but never the whole name.
  while (tokens.length > 1 && /^\d+$/.test(tokens[tokens.length - 1]!)) tokens.pop();
  // Drop trailing tokens that are a mix of letters+digits typical of terminal ids ("se0123", "k3").
  while (tokens.length > 1 && /^[a-z]{0,2}\d{2,}[a-z]?$/.test(tokens[tokens.length - 1]!)) tokens.pop();
  return tokens.join(' ');
}

/** Tokens of a merchant key with at least three characters (for overlap scoring). */
export function merchantTokens(name: string): Set<string> {
  return new Set(
    merchantKeyOf(name)
      .split(' ')
      .filter((t) => t.length >= 3),
  );
}

/**
 * Do two names share a meaningful token? "CLAS OHLSON AB" vs bank text
 * "K*CLAS OHLSON 023" → true. Very short names compare as whole keys.
 */
export function merchantTokensOverlap(a: string, b: string): boolean {
  const ka = merchantKeyOf(a);
  const kb = merchantKeyOf(b);
  if (!ka || !kb) return false;
  if (ka === kb) return true;
  if (ka.includes(kb) || kb.includes(ka)) return true;
  const ta = merchantTokens(a);
  for (const t of merchantTokens(b)) if (ta.has(t)) return true;
  return false;
}

/** Display name for a rule: the receipt's spelling, trimmed, first letter kept as printed. */
export function merchantDisplayName(name: string): string {
  return name.replace(/\s+/g, ' ').trim();
}
